import React from 'react';

import { useAppSelector } from '@/store';

import {
  GAME_CATALOG_SOURCE_ID,
  gameIdsOfSource,
  gameListSourceId,
  gamesOfSource,
  resolveGamesByIds,
  type GameSourceRecord,
  type GameTab,
} from './game-sources';
import { cachedGameSource, fetchGameSource, type GameSourceBlob, type GameSourceOptions } from './games-api';

/** The catalogue is read SLIM: every game, without its art (see `games-api`). */
const SLIM: GameSourceOptions = { view: 'slim' };

export interface UseGameSources {
  /** The tab's games, as SLIM records (see `catalog`). */
  games: GameSourceRecord[];
  /** The whole tenant-0 guest catalogue, regardless of the active tab, as SLIM records: id, name, provider_id and
   *  the type / subtype / visual-theme attributes, NO art bundle and no url. Lets a consumer resolve arbitrary game
   *  ids to records (e.g. pre-selecting an ad's already-chosen games when editing) and filter them. Art comes by id:
   *  `GameThumb` / `useGameThumb` for a thumbnail, `useGameRecords` / `fetchGamesByIds` for the full records.
   *  (An older spark ignores the view and answers full records, art included; the art readers use it as is.) */
  catalog: GameSourceRecord[];
  /** The data needed for this tab has not resolved yet. */
  loading: boolean;
  /** The last read of the catalogue failed (reset as a new one starts). A failed read settles `loading` with an empty
   *  catalogue, which reads exactly like a catalogue with no games without this. */
  failed: boolean;
}

/** The curated-list source's state, KEYED to the source id it holds: a tab switch reads a stale id as
 *  not-loaded, so the Top tab never briefly renders the Popular tab's games (and vice versa). */
interface ListState {
  id: string;
  blob?: GameSourceBlob;
  settled: boolean;
}

/**
 * `attempt` is a retry: changing it re-runs the catalogue fetch. The fetch settles on failure by design
 * (so the picker never hangs), which leaves the catalogue empty with nothing to retry it, and a caller that
 * cannot render without it has no way out otherwise.
 *
 * `enabled` false holds the fetch back entirely, for a caller that only needs the catalogue under a
 * condition it cannot know at hook-call time. Even slim, the catalogue is every game there is, so a screen
 * that merely MIGHT need it should not pay for it on every visit.
 *
 * Loads a game browse tab. Every tab needs the tenant-0 guest CATALOGUE (`all_slot_games_api`, read slim:
 * no art); the Popular / Top tabs additionally load a curated ORDERING source (an array of game-id strings)
 * and resolve those ids against the catalogue.
 *
 * Read through spark-creatives (server-side, service token, see `games-api`), NOT the config-engine
 * `fetchSources` thunk: that reads the platform data cluster from the browser with a per-brand GUEST token,
 * which 401s on prod and (mis-read as an owner-session expiry) signs the owner out. The blobs are held
 * locally and backed by a module-level cache so the catalogue is fetched once per brand across the
 * picker's many conditional remounts. `loading` settles to false on a fetch error too, so the picker falls
 * through to its empty state instead of hanging on a spinner.
 */
export const useGameSources = (tab: GameTab, enabled = true, attempt = 0): UseGameSources => {
  // Select the RAW value and coerce outside the selector: `Number(undefined)` is NaN, and NaN !== NaN, so a
  // selector returning NaN would be seen as changed on every unrelated dispatch and re-render the picker.
  const brandTenant = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const tenantRaw = Number(brandTenant);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const listId = tab === 'all' ? undefined : gameListSourceId(tab);

  // The catalogue is the global (tenant-0) source, so a single slot is safe; seed from the module cache so a
  // remount with it already fetched paints without a flash.
  const [catalogBlob, setCatalogBlob] = React.useState<GameSourceBlob | undefined>(() =>
    tenantId ? cachedGameSource(tenantId, GAME_CATALOG_SOURCE_ID, SLIM) : undefined,
  );
  const [catalogSettled, setCatalogSettled] = React.useState(false);
  const [catalogFailed, setCatalogFailed] = React.useState(false);

  // The list changes with the tab (popular <-> top), so its state is KEYED to the source id it holds: a tab
  // switch reads as not-loaded (spinner) until THIS tab's own blob lands, never another tab's games.
  const [list, setList] = React.useState<ListState | undefined>(() => {
    if (!tenantId || !listId) return undefined;
    const cached = cachedGameSource(tenantId, listId);
    return cached ? { id: listId, blob: cached, settled: true } : undefined;
  });
  const listBlob = list && list.id === listId ? list.blob : undefined;
  const listSettled = Boolean(list && list.id === listId && list.settled);

  React.useEffect(() => {
    if (!tenantId || !enabled) return undefined;
    let stale = false;
    setCatalogSettled(false);
    setCatalogFailed(false);
    const cached = cachedGameSource(tenantId, GAME_CATALOG_SOURCE_ID, SLIM);
    if (cached) setCatalogBlob(cached);
    fetchGameSource(tenantId, GAME_CATALOG_SOURCE_ID, SLIM)
      .then((blob) => {
        if (!stale) setCatalogBlob(blob);
      })
      .catch(() => {
        // Keep the last-known catalogue on a transient error, and say the read failed; settle below so the picker
        // never hangs.
        if (!stale) setCatalogFailed(true);
      })
      .finally(() => {
        if (!stale) setCatalogSettled(true);
      });
    return () => {
      stale = true;
    };
  }, [tenantId, enabled, attempt]);

  React.useEffect(() => {
    if (!tenantId || !listId) {
      setList(undefined);
      return undefined;
    }
    const forId = listId;
    let stale = false;
    const cached = cachedGameSource(tenantId, forId);
    if (cached) {
      setList({ id: forId, blob: cached, settled: true });
      return undefined;
    }
    setList({ id: forId, blob: undefined, settled: false }); // this tab is loading
    fetchGameSource(tenantId, forId)
      .then((blob) => {
        if (!stale) setList({ id: forId, blob, settled: true });
      })
      .catch(() => {
        // Settle to empty (not another tab's data) so the tab never hangs and never cross-shows.
        if (!stale) setList({ id: forId, blob: undefined, settled: true });
      });
    return () => {
      stale = true;
    };
  }, [tenantId, listId]);

  const catalog = React.useMemo(() => gamesOfSource(catalogBlob), [catalogBlob]);

  const games = React.useMemo(() => {
    if (tab === 'all') return catalog;
    return resolveGamesByIds(gameIdsOfSource(listBlob), catalog);
  }, [tab, catalog, listBlob]);

  // Loading only while we have NOTHING to show AND the fetch has not settled: a cache hit paints
  // immediately, and an error settles to the empty state rather than a permanent spinner.
  const catalogLoading = catalogBlob === undefined && !catalogSettled;
  const listLoading = tab !== 'all' && listBlob === undefined && !listSettled;
  const loading = tenantId === undefined || catalogLoading || listLoading;

  return { games, catalog, loading, failed: catalogFailed };
};

export default useGameSources;
